import { ref } from "vue";
import { useFetch } from "@/core/api/common/composables/useFetch";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";

// Reactive flag indicating whether a LivePerson co-browsing session is active.
// When true, VcIcon switches from external <use href> to inlined SVG paths
// so the representative's reconstructed DOM can render the icons.
export const isCoBrowsingActive = ref(false);

let _coBrowseInterval: ReturnType<typeof setInterval> | null = null;

// Detect an active LivePerson co-browse session.
// Primary signal: lpTag.isCobrowsingActive (set by LP while sharing the screen).
// Fallback: LP injects co-browse-only DOM markers (border frame + status node).
function _isCoBrowsingActive(): boolean {
  if (window.lpTag?.isCobrowsingActive === true) {
    return true;
  }
  return !!document.getElementById("cobrowsingstatus") || !!document.getElementById("cobrowsing-border-top");
}

function _tickCoBrowseState(): void {
  isCoBrowsingActive.value = _isCoBrowsingActive();
}

export function initCoBrowseDetection(): void {
  // Guard: clear any existing interval before starting a new one.
  if (_coBrowseInterval !== null) {
    clearInterval(_coBrowseInterval);
    _coBrowseInterval = null;
  }

  // Run an immediate first tick so state is correct without waiting 1s.
  _tickCoBrowseState();

  _coBrowseInterval = setInterval(_tickCoBrowseState, 1000);
}

export function cleanupCoBrowseDetection(): void {
  if (_coBrowseInterval !== null) {
    clearInterval(_coBrowseInterval);
    _coBrowseInterval = null;
  }
  isCoBrowsingActive.value = false;
}

export function useLivePerson() {
  function shouldShowChat(isStaticPage: boolean, staticRoute: string | undefined, pageId: number | undefined): boolean {
    //default value is show on both CMS and static pages
    const showPerCMSPage = isStaticPage
      ? false
      : pageId
        ? !globals.livePersonConfigOnCurrentLanguage?.exceptCmsPageIds?.includes(pageId)
        : true;
    const showPerStaticPage =
      isStaticPage &&
      (staticRoute ? !globals.livePersonConfigOnCurrentLanguage?.exceptStaticPageRoutes?.includes(staticRoute) : true);

    return (
      (globals.livePersonConfigOnCurrentLanguage?.enableOnLanguage ?? false) && (showPerStaticPage || showPerCMSPage)
    );
  }

  function startLivePerson(showChatOnPage: boolean) {
    if (!window.lpTag?.started && showChatOnPage) {
      window.lpTag?.start();
    }
  }

  function openLivePersonChat() {
    const bubble = document.querySelector('.bubble-container [data-lp-event="click"]');
    if (bubble) {
      bubble.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
    }
  }

  return {
    startLivePerson: startLivePerson,
    shouldShowChat: shouldShowChat,
    openLivePersonChat: openLivePersonChat,
  };
}

export const showChatFooter = ref(false);

// True while the LP chat window is active (open or minimized).
export const isChatWindowActive = ref(false);

let _chatStateInterval: ReturnType<typeof setInterval> | null = null;
let _closedCount = 0;
// Require many consecutive "closed" readings before showing bubble back.
// This prevents flickering from LP's internal animations/resizing.
const CLOSED_THRESHOLD = 3; // 3 × 500ms = 1.5 seconds of confirmed "closed"

function _isLpWindowOpen(): boolean {
  // LP's actual chat window uses .lp_maximized (open) or .lp_minimized (minimized).
  // These have real dimensions only when the chat is active.
  const maximized = document.querySelector(".lp_maximized") as HTMLElement | null;
  if (maximized && maximized.offsetHeight > 0) {
    return true;
  }

  const minimized = document.querySelector(".lp_minimized") as HTMLElement | null;
  return !!(minimized && minimized.offsetHeight > 0);
}

function _tickChatState(): void {
  const isOpen = _isLpWindowOpen();

  if (isOpen) {
    isChatWindowActive.value = true;
    _closedCount = 0;
  } else if (isChatWindowActive.value) {
    _closedCount++;
    if (_closedCount >= CLOSED_THRESHOLD) {
      isChatWindowActive.value = false;
      _closedCount = 0;
    }
  }
}

export function initChatWindowObserver(): void {
  // Guard: clear any existing interval before starting a new one.
  if (_chatStateInterval !== null) {
    clearInterval(_chatStateInterval);
    _chatStateInterval = null;
  }

  // Run an immediate first tick so state is correct without waiting 500ms.
  _tickChatState();

  _chatStateInterval = setInterval(_tickChatState, 500);
}

export function cleanupChatWindowObserver(): void {
  if (_chatStateInterval !== null) {
    clearInterval(_chatStateInterval);
    _chatStateInterval = null;
  }
  _closedCount = 0;
  isChatWindowActive.value = false;
}

// Whether any LivePerson agent in the configured skills is currently online.
// Defaults to true (fail-open) so the bubble is never wrongly hidden before
// the first poll resolves or when the backend cannot be reached.
export const agentsAvailable = ref(true);

let _availabilityTimer: ReturnType<typeof setTimeout> | null = null;
let _pollingActive = false;

// Poll cadence is driven by the server's cache duration (LivePerson.CacheDurationSeconds
// store setting) so the client never polls more often than the value can change.
// The server value is clamped to a sane range as a safety net.
const DEFAULT_POLL_INTERVAL = 60_000;
const MIN_POLL_INTERVAL = 15_000;
const MAX_POLL_INTERVAL = 30 * 60_000;
let _pollIntervalMs = DEFAULT_POLL_INTERVAL;

// Upper bound for the initial requestIdleCallback deferral so the first poll always runs.
const IDLE_CALLBACK_TIMEOUT = 3_000;

interface IAgentAvailabilityResponse {
  available: boolean;
  cacheDurationSeconds?: number;
}

function _resolvePollInterval(cacheDurationSeconds: number | undefined): number {
  if (typeof cacheDurationSeconds !== "number" || !Number.isFinite(cacheDurationSeconds) || cacheDurationSeconds <= 0) {
    return DEFAULT_POLL_INTERVAL;
  }
  return Math.min(Math.max(cacheDurationSeconds * 1000, MIN_POLL_INTERVAL), MAX_POLL_INTERVAL);
}

function _handleAgentAvailabilityError(error: unknown): void {
  Logger.error("useLivePerson: failed to fetch agent availability", error);
  agentsAvailable.value = true;
}

async function _fetchAgentAvailability(): Promise<void> {
  try {
    const { data } = await useFetch("/api/live-person/agent-availability", {
      onFetchError: (context) => {
        _handleAgentAvailabilityError(context.error);
        return context;
      },
    })
      .get()
      .json<IAgentAvailabilityResponse>();
    if (typeof data.value?.available === "boolean") {
      agentsAvailable.value = data.value.available;
    }
    // Align the next poll with the server-side cache duration.
    _pollIntervalMs = _resolvePollInterval(data.value?.cacheDurationSeconds);
  } catch (e) {
    // Fail open: keep the bubble's existing behaviour on any error.
    _handleAgentAvailabilityError(e);
  }
}

function _scheduleIdle(cb: () => void): void {
  if (typeof window.requestIdleCallback === "function") {
    // Cap the idle deferral so sustained main-thread activity can never starve the
    // first poll indefinitely (which would leave agentsAvailable stuck at its default).
    window.requestIdleCallback(cb, { timeout: IDLE_CALLBACK_TIMEOUT });
  } else {
    window.setTimeout(cb, 0);
  }
}

function _pollOnce(): void {
  void (async () => {
    await _fetchAgentAvailability();
    _scheduleNextPoll();
  })();
}

function _scheduleNextPoll(): void {
  if (!_pollingActive) {
    return;
  }
  _availabilityTimer = setTimeout(_pollOnce, _pollIntervalMs);
}

export function startAgentAvailabilityPolling(): void {
  stopAgentAvailabilityPolling();
  _pollingActive = true;

  // Defer the first request until the browser is idle so it never competes
  // with the critical page-load path (initial render, hydration, etc.).
  // Each subsequent poll is scheduled from the server's cache duration.
  _scheduleIdle(_pollOnce);
}

export function stopAgentAvailabilityPolling(): void {
  _pollingActive = false;
  if (_availabilityTimer !== null) {
    clearTimeout(_availabilityTimer);
    _availabilityTimer = null;
  }
}
